import type {EditorView} from '@codemirror/view'
import type {Element, ElementContent} from 'hast'
import {toDom} from 'hast-util-to-dom'
import {h} from 'hastscript'

import {getTextContent} from '../../language-tools/html/hast-utils'
import {isKnownValidVideoUrl, registerInvalidVideoUrl, registerValidVideoUrl} from '../extensions/video-registry'
import {HastElementWidget} from './hast-element'
import styles from './video-link.module.css'

/**
 * The only way to find out whether some URL points at a valid accessible video resource is to try and render it into
 * a video element. So that's what this widget does, updating the video URL registry on success/failure. On failure,
 * we should just render the URL as editable text instead of a widget.
 */
export class VideoLinkWidget extends HastElementWidget {
  #src: string
  #label: string
  #content: ElementContent[]

  constructor(link: Element, className?: string) {
    const src = link.properties.href as string
    const label = getTextContent(link)
    const content = link.children

    super(
      h('video', {
        src,
        controls: true,
        playsInline: true,
        ariaLabel: label,
        preload: 'metadata',
      }),
      className,
    )

    this.#src = src
    this.#label = label
    this.#content = content
  }

  override toDOM(view: EditorView) {
    const isKnownValidUrl = isKnownValidVideoUrl(view.state, this.#src)

    const container = document.createElement('span')

    const loadingPlaceholder = toDom(
      h('a', {href: this.#src, class: styles.placeholder}, this.#content),
    ) as HTMLAnchorElement

    const videoElement = super.toDOM(view) as HTMLVideoElement

    videoElement.addEventListener('error', () => {
      registerInvalidVideoUrl(view, this.#src)
    })

    videoElement.addEventListener('canplaythrough', () => {
      videoElement.style.display = 'block'
      loadingPlaceholder.style.display = 'none'
      registerValidVideoUrl(view, this.#src)
    })

    if (!isKnownValidUrl) videoElement.style.display = 'none'
    else loadingPlaceholder.style.display = 'none'

    container.replaceChildren(loadingPlaceholder, videoElement)
    return container
  }
}
